/* ============================================================
   OnlyLewds — Cart Drawer  v4
   Strategy: instead of fighting Dawn's product-form web
   component, we watch for cart changes via a smart poller +
   Shopify's document custom events, and re-render everything
   from /cart.js on every change.
   ============================================================ */
(function () {
  'use strict';

  /* ── CONFIG ── */
  var DIGITAL_TAG   = 'digital';
  var DISC_CODE     = 'VAULT20';   // 20% off all products at $300 — all cart modes
  var D_THRESH      = 30000;   // $300 → 20% off (all modes)
  var P_THRESH      = 28500;   // US$200 → free shipping; rule fires about CAD 279 (10-31), 285 leaves room for FX
  var CART_URL     = '/cart.js';
  var ADD_URL      = '/cart/add.js';
  var CHANGE_URL   = '/cart/change.js';
  var CHECKOUT_URL = '/checkout';

  /* ── STATE ── */
  var lastItemCount    = -1;
  var lastCartTotal    = -1;
  var cartMode         = 'digital';
  var discountActive   = false;
  var shippingUnlocked = false;
  var confettiFired    = { digital: false, physical: false };
  var drawerOpen       = false;
  // tagCache: productId (string) → comma-separated tag string
  // Seeded from Liquid data-tags on page load, persists across AJAX refreshes
  var tagCache = {};

  /* ── REFS ── */
  function $id(id) { return document.getElementById(id); }

  /* ============================================================
     PUBLIC API
  ============================================================ */
  window.OLCart = {
    open:     openDrawer,
    close:    closeDrawer,
    addItem:  addItem,
    refresh:  fetchAndRender,
    _changeQty: changeQty,
    _addUpsell: addUpsell,
    _showToast: showToast,
  };

  /* ============================================================
     OPEN / CLOSE
  ============================================================ */
  function openDrawer() {
    fetchAndRender().then(function () {
      var d = $id('ol-cart-drawer');
      var o = $id('ol-cart-overlay');
      if (d) { d.classList.add('ol-open'); drawerOpen = true; }
      if (o) o.classList.add('ol-active');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeDrawer() {
    var d = $id('ol-cart-drawer');
    var o = $id('ol-cart-overlay');
    if (d) { d.classList.remove('ol-open'); drawerOpen = false; }
    if (o) o.classList.remove('ol-active');
    document.body.style.overflow = '';
    lastUpsellMode = null; // force fresh upsell fetch on next open
  }

  /* ============================================================
     ADD ITEM (called from product page OLCart.addItem())
  ============================================================ */
  function addItem(variantId, qty) {
    qty = qty || 1;
    return fetch(ADD_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ id: variantId, quantity: qty }),
    }).then(function () {
      showToast('✦ Added to your vault');
      openDrawer();
    });
  }

  /* ============================================================
     CHANGE QTY / REMOVE
  ============================================================ */
  function changeQty(key, qty) {
    fetch(CHANGE_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ id: key, quantity: qty }),
    }).then(fetchAndRender);
  }

  /* ============================================================
     UPSELL ADD
  ============================================================ */
  function addUpsell(variantId, btn) {
    btn.textContent = '...';
    btn.disabled    = true;
    fetch(ADD_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ id: variantId, quantity: 1 }),
    }).then(function () {
      btn.textContent = '✓';
      btn.classList.add('ol-btn-added');
      showToast('✦ Added to your vault');
      fetchAndRender();
    }).catch(function () {
      btn.textContent = 'Add';
      btn.disabled    = false;
    });
  }

  /* ============================================================
     FETCH AND RENDER
     Single source of truth — called after every cart mutation
  ============================================================ */
  function fetchAndRender() {
    return fetch(CART_URL, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartMode = detectMode(cart);
        renderAll(cart);
        lastItemCount = cart.item_count;
        lastCartTotal = cart.total_price;
      });
  }

  /* ============================================================
     MODE DETECTION
     digital  — all items have 'digital' tag
     physical — all items are physical (no digital tag)
     mixed    — cart has both digital and physical items
  ============================================================ */
  function detectMode(cart) {
    if (!cart.items || cart.items.length === 0) return 'digital';
    var hasDigital  = false;
    var hasPhysical = false;
    for (var i = 0; i < cart.items.length; i++) {
      var pid  = String(cart.items[i].product_id);
      var raw  = tagCache[pid];
      if (raw !== undefined) {
        var tags = raw.split(',').map(function(t){ return t.trim().toLowerCase(); });
        if (tags.indexOf(DIGITAL_TAG) !== -1) {
          hasDigital = true;
        } else {
          hasPhysical = true;
        }
      }
      // uncached = skip, let it resolve after tag fetch
    }
    if (hasDigital && hasPhysical) return 'mixed';
    if (hasPhysical) return 'physical';
    return 'digital'; // all digital or all uncached
  }

  /* ============================================================
     RENDER ALL
  ============================================================ */
  function renderAll(cart) {
    renderModePill();
    renderItems(cart);
    renderThreshold(cart);
    renderUpsells(cart);
    renderFooter(cart);
    updateNavBadge(cart);
  }

  /* ── MODE PILL ── */
  function renderModePill() {
    var pill = $id('ol-mode-pill');
    if (!pill) return;
    var dr = $id('ol-cart-drawer');
    var label = cartMode === 'digital' ? 'Digital' : cartMode === 'mixed' ? 'Mixed' : 'Physical';
    pill.textContent = label;
    pill.className   = 'ol-mode-pill ol-' + cartMode;
    if (dr) dr.setAttribute('data-mode', cartMode);
  }

  /* ── ITEMS ── */
  function renderItems(cart) {
    var container = $id('ol-cart-items');
    if (!container) return;

    // Snapshot existing data-tags and data-product-id by key before overwriting innerHTML
    var tagMap = {};
    container.querySelectorAll('.ol-cart-item[data-key]').forEach(function (el) {
      var key = el.getAttribute('data-key');
      var pid = el.getAttribute('data-product-id');
      var tags = el.getAttribute('data-tags') || '';
      tagMap[key] = tags;
      // Keep tagCache up to date
      if (pid && tags) tagCache[pid] = tags;
    });

    if (!cart.items || cart.items.length === 0) {
      container.innerHTML =
        '<div class="ol-empty-state">' +
        '<div class="ol-empty-icon">🗂️</div>' +
        '<div class="ol-empty-title">Your vault is empty</div>' +
        '<div class="ol-empty-sub">Add something from the store to get started.</div>' +
        '</div>';
      return;
    }

    container.innerHTML = cart.items.map(function (item) {
      var savedTags = tagMap[item.key] || tagCache[String(item.product_id)] || '';
      var tags      = savedTags.split(',').map(function (t) { return t.trim().toLowerCase(); });
      var isDigital = tags.length > 0 && savedTags !== ''
        ? tags.indexOf(DIGITAL_TAG) !== -1
        : false; // unknown = treat as physical (safe default)

      var ribbonCls = isDigital ? 'ol-digital' : 'ol-physical';
      var ribbonTxt = isDigital ? '✦ Instant Access' : '✦ Limited Edition';
      var variant   = (item.variant_title && item.variant_title !== 'Default Title')
        ? item.variant_title
        : (isDigital ? 'Digital · Instant Access' : 'Collector\'s Edition · Physical');

      return '<div class="ol-cart-item"' +
        ' data-key="' + esc(item.key) + '"' +
        ' data-product-id="' + item.product_id + '"' +
        ' data-tags="' + esc(savedTags) + '">' +
        '<div class="ol-item-thumb">' +
          (item.image
            ? '<img src="' + item.image + '" alt="' + esc(item.product_title) + '" loading="lazy">'
            : '<span style="font-size:22px">📸</span>') +
          '<div class="ol-item-ribbon ' + ribbonCls + '">' + ribbonTxt + '</div>' +
        '</div>' +
        '<div>' +
          '<div class="ol-item-brand">Only Lewds</div>' +
          '<div class="ol-item-name">' + esc(item.product_title) + '</div>' +
          '<div class="ol-item-variant">' + esc(variant) + '</div>' +
          '<div class="ol-item-qty">' +
            '<button type="button" class="ol-qty-btn"' +
              ' data-action="decrease" data-key="' + esc(item.key) + '"' +
              ' data-qty="' + (item.quantity - 1) + '" aria-label="Decrease">−</button>' +
            '<span class="ol-qty-val">' + item.quantity + '</span>' +
            '<button type="button" class="ol-qty-btn"' +
              ' data-action="increase" data-key="' + esc(item.key) + '"' +
              ' data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="ol-item-price">' +
          '<span>' + money(item.line_price, true) + '</span>' +
          '<button type="button" class="ol-remove-btn"' +
            ' data-action="remove" data-key="' + esc(item.key) + '"' +
            ' aria-label="Remove">✕</button>' +
        '</div>' +
        '</div>';
    }).join('');
  }

  /* ── THRESHOLD ── */
  function renderThreshold(cart) {
    var dBar = $id('ol-d-bar');
    var pBar = $id('ol-p-bar');
    if (!dBar || !pBar) return;

    // Always use total cart price for threshold tracking —
    // mixed carts get both rewards based on combined spend
    var total = cart.total_price || 0;

    /* ── DIGITAL BAR (20% off at $300 — all modes) ── */
    var showDigital = true; // always show — all cart types can earn 20% off
    dBar.classList.toggle('ol-hidden', !showDigital);

    if (showDigital) {
      // $300 reward counts product prices BEFORE discounts — Shopify checks VAULT20's minimum the same way
      // (verified 2026-09-23: $324.98 cart that the photobook 10% takes to $296.99 still gets VAULT20).
      var dTotal = (cart.original_total_price != null) ? cart.original_total_price : total;
      var dRem = Math.max(0, D_THRESH - dTotal);
      var dPct = Math.min(100, (dTotal / D_THRESH) * 100);
      var fill = $id('ol-d-fill'), amt = $id('ol-d-amt'), sub = $id('ol-d-sub'), rev = $id('ol-disc-reveal');

      if (fill) fill.style.width = dPct + '%';
      if (amt)  amt.textContent  = money(dTotal, true) + ' / ' + moneyThresh(D_THRESH);

      var wasActive  = discountActive;
      discountActive = dRem <= 0;

      if (sub) {
        sub.innerHTML = discountActive
          ? '🎉 <strong>20% off unlocked</strong> — auto-applied at checkout'
          : 'Add <strong>' + moneyThresh(dRem) + '</strong> more to unlock <strong>20% off this order</strong>' +
            '<span class="ol-thresh-no-combine">Cannot combine with other discounts</span>';
      }
      if (rev) rev.classList.toggle('ol-hidden', !discountActive);
      if (!wasActive && discountActive) { confettiFired.digital = false; }
      if (discountActive && !confettiFired.digital) { fireConfetti(); confettiFired.digital = true; }
      if (!discountActive) confettiFired.digital = false;
    } else {
      discountActive = false;
    }

    /* ── PHYSICAL BAR (free shipping at $200) ── */
    var showPhysical = (cartMode === 'physical' || cartMode === 'mixed');
    pBar.classList.toggle('ol-hidden', !showPhysical);

    if (showPhysical) {
      var pRem = Math.max(0, P_THRESH - total);
      var pPct = Math.min(100, (total / P_THRESH) * 100);
      var pFill = $id('ol-p-fill'), pAmt = $id('ol-p-amt'), pSub = $id('ol-p-sub');

      if (pFill) pFill.style.width = pPct + '%';
      if (pAmt)  pAmt.textContent  = money(total, true) + ' / ' + moneyThresh(P_THRESH);

      var wasUnlocked  = shippingUnlocked;
      shippingUnlocked = pRem <= 0;

      if (pSub) {
        pSub.innerHTML = shippingUnlocked
          ? '🎉 <strong>Free tracked shipping unlocked!</strong>'
          : 'Add <strong>' + moneyThresh(pRem) + '</strong> more to unlock <strong>free tracked shipping</strong>';
      }
      if (!wasUnlocked && shippingUnlocked) { confettiFired.physical = false; }
      if (shippingUnlocked && !confettiFired.physical) { fireConfetti(); confettiFired.physical = true; }
      if (!shippingUnlocked) confettiFired.physical = false;
    }
  }

  /* ── UPSELLS ──
     The upsell snippet renders once server-side into #ol-upsell-wrap.
     On AJAX refresh we re-fetch the cart page and extract fresh upsell HTML.
     JS also hides cards whose product is already in cart.
  */
  var lastUpsellMode = null;

  function renderUpsells(cart) {
    var wrap = $id('ol-upsell-wrap');
    if (!wrap) return;

    var inCart = new Set((cart.items || []).map(function (i) { return i.product_id; }));

    // If cart is empty, clear upsells
    if (!cart.items || cart.items.length === 0) {
      wrap.innerHTML = '';
      lastUpsellMode = null;
      return;
    }

    // If mode changed since last render, re-fetch fresh upsell HTML from the cart page
    if (lastUpsellMode !== cartMode) {
      lastUpsellMode = cartMode;
      fetchFreshUpsells(cart, wrap, inCart);
      return;
    }

    // Same mode — just hide/show cards based on what's in cart
    wrap.querySelectorAll('[data-upsell-product-id]').forEach(function (card) {
      card.classList.toggle('ol-hidden', inCart.has(parseInt(card.getAttribute('data-upsell-product-id'), 10)));
    });
  }

  function fetchFreshUpsells(cart, wrap, inCart) {
    // Fetch the cart page which re-renders all Liquid including upsells
    fetch('/cart', {
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
    })
    .then(function(r) { return r.text(); })
    .then(function(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var freshWrap = doc.getElementById('ol-upsell-wrap');
      if (freshWrap) {
        wrap.innerHTML = freshWrap.innerHTML;
        // Hide already-in-cart cards
        wrap.querySelectorAll('[data-upsell-product-id]').forEach(function (card) {
          card.classList.toggle('ol-hidden', inCart.has(parseInt(card.getAttribute('data-upsell-product-id'), 10)));
        });
      }
    })
    .catch(function() {
      // Fallback: just hide/show existing cards
      wrap.querySelectorAll('[data-upsell-product-id]').forEach(function (card) {
        card.classList.toggle('ol-hidden', inCart.has(parseInt(card.getAttribute('data-upsell-product-id'), 10)));
      });
    });
  }

  /* ── FOOTER ── */
  function renderFooter(cart) {
    var amt  = $id('ol-subtotal-amount');
    var note = $id('ol-footer-note');
    var btn  = $id('ol-checkout-btn');
    var currNote  = $id('ol-currency-note');
    var currLabel = $id('ol-currency-label');

    // Currency disclosure — only show when Beast has a non-CAD currency active
    if (currNote && currLabel) {
      var dgc = window.DoublyGlobalCurrency;
      var shopCur = getShopCurrency();
      var activeCur = dgc && dgc.currentCurrency ? dgc.currentCurrency : shopCur;
      if (activeCur && activeCur !== shopCur) {
        currLabel.textContent = activeCur;
        currNote.style.display = '';
      } else {
        currNote.style.display = 'none';
      }
    }
    if (amt)  amt.textContent  = money(cart.total_price || 0, true);
    if (note) note.textContent = cartMode === 'digital'
      ? 'No shipping · Instant digital access'
      : 'Taxes & tracked shipping calculated at checkout';
    if (btn) {
      var url = discountActive ? CHECKOUT_URL + '?discount=' + DISC_CODE : CHECKOUT_URL;
      btn.href = url;
      btn.setAttribute('data-checkout-url', url);
    }
    renderFxHint(cart);
  }

  /* ── FX HINT (non-Canadian shoppers still viewing CAD): "Charged in CAD · about US$X" ──
     Rates come from ol-currency.js's 24h cache (localStorage ol_fx_rates). Preview-gated by data-prev. */
  function renderFxHint(cart) {
    var el = $id('ol-fx-hint');
    if (!el) return;
    try {
      if (el.getAttribute('data-prev') !== '1') { el.style.display = 'none'; return; }
      var country = (window.Shopify && window.Shopify.country) || '';
      var cur = localStorage.getItem('ol_currency') || 'CAD';
      if (country === 'CA' || cur !== 'CAD' || !cart.total_price) { el.style.display = 'none'; return; }
      var eu = ['AT','BE','CY','DE','EE','ES','FI','FR','GR','HR','IE','IT','LT','LU','LV','MT','NL','PT','SI','SK'];
      var code = ({ US: 'USD', GB: 'GBP', AU: 'AUD' })[country] || (eu.indexOf(country) > -1 ? 'EUR' : 'USD');
      var o = JSON.parse(localStorage.getItem('ol_fx_rates') || 'null');
      var r = o && o.r && o.r[code];
      if (!r) { el.style.display = 'none'; return; }
      var sym = ({ USD: 'US$', GBP: '£', AUD: 'A$', EUR: '€' })[code];
      el.textContent = 'Charged in CAD · about ' + sym + (cart.total_price / 100 * r).toFixed(2) + ' · your bank converts it';
      el.style.display = '';
    } catch (e) { el.style.display = 'none'; }
  }

  /* ── NAV BADGE ── */
  function updateNavBadge(cart) {
    var count = cart.item_count || 0;
    document.querySelectorAll('[data-ol-cart-count]').forEach(function (el) {
      el.textContent = count;
      var bubble = el.closest('.cart-count-bubble') || el.parentElement;
      if (bubble) bubble.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ============================================================
     CONFETTI
  ============================================================ */
  function fireConfetti() {
    var colors = ['#ffb3c4','#ff97ad','#e8457a','#fff','#ffd6e0'];
    var dr   = $id('ol-cart-drawer');
    var rect = dr ? dr.getBoundingClientRect() : { left: window.innerWidth/2-200, top: 60, width: 400 };
    for (var i = 0; i < 80; i++) {
      (function () {
        var el     = document.createElement('div');
        var x      = rect.left + Math.random() * rect.width;
        var y      = rect.top  + 60;
        var color  = colors[Math.floor(Math.random() * colors.length)];
        var size   = 6 + Math.random() * 7;
        var dur    = 0.8 + Math.random() * 0.7;
        var delay  = Math.random() * 0.25;
        el.style.cssText =
          'position:fixed;left:'+x+'px;top:'+y+'px;' +
          'width:'+size+'px;height:'+size+'px;' +
          'background:'+color+';border-radius:'+(Math.random()>.5?'50%':'2px')+';' +
          'pointer-events:none;z-index:99999;opacity:1;' +
          'transform:rotate('+(Math.random()*360)+'deg);' +
          'transition:transform '+dur+'s ease '+delay+'s,' +
            'top '+dur+'s ease '+delay+'s,' +
            'opacity 0.4s ease '+(delay+dur*.6)+'s;';
        document.body.appendChild(el);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.style.top       = (y + 130 + Math.random() * 160) + 'px';
            el.style.opacity   = '0';
            el.style.transform = 'rotate('+(Math.random()*720)+'deg) translateX('+((Math.random()-.5)*120)+'px)';
          });
        });
        setTimeout(function () { if (el.parentNode) el.remove(); }, (dur+delay+0.5)*1000);
      }());
    }
  }

  /* ============================================================
     TOAST
  ============================================================ */
  var toastTimer = null;
  function showToast(msg) {
    var t = $id('ol-toast'), m = $id('ol-toast-msg');
    if (!t || !m) return;
    m.textContent = msg;
    t.classList.add('ol-show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('ol-show'); }, 2800);
  }

  /* ============================================================
     HELPERS
  ============================================================ */
  /* ── CURRENCY CONVERSION (Beast / DoublyGlobalCurrency) ── */
  function getShopCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || window.shopCurrency
      || 'CAD';
  }

  function _formatConverted(amount, currencyCode, forceDecimals) {
    var zeroDecimal = ['JPY','KRW','VND','IDR','CLP','ISK','HUF','TWD'].indexOf(currencyCode) !== -1;
    var decimals = (zeroDecimal || !forceDecimals) ? 0 : 2;
    if (window.Intl && window.Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(amount);
      } catch(e) {}
    }
    return currencyCode + ' ' + (decimals ? amount.toFixed(2) : Math.round(amount));
  }

  /* Convert cents (in shop base currency) to display string in user's currency.
     Rounds to .99 for cart item prices, whole numbers for threshold amounts. */
  function money(cents, roundToNinetyNine) {
    var amount  = cents / 100;
    var dgc     = window.DoublyGlobalCurrency;
    var shopCur = getShopCurrency();

    if (dgc && dgc.currentCurrency && dgc.currentCurrency !== shopCur && typeof dgc.convert === 'function') {
      try {
        var converted = dgc.convert(amount, shopCur, dgc.currentCurrency);
        if (roundToNinetyNine) {
          var zeroDecimal = ['JPY','KRW','VND','IDR','CLP','ISK','HUF','TWD'].indexOf(dgc.currentCurrency) !== -1;
          if (zeroDecimal) {
            converted = Math.round(converted);
          } else {
            converted = Math.floor(converted) + 0.99;
          }
        }
        return _formatConverted(converted, dgc.currentCurrency, roundToNinetyNine);
      } catch(e) {}
    }

    // Fallback: store default currency
    var fallback = amount;
    if (roundToNinetyNine) {
      fallback = Math.floor(fallback) + 0.99;
    }
    if (window.Intl && window.Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: shopCur,
          minimumFractionDigits: roundToNinetyNine ? 2 : 0,
          maximumFractionDigits: roundToNinetyNine ? 2 : 0
        }).format(fallback);
      } catch(e) {}
    }
    return '$' + (roundToNinetyNine ? fallback.toFixed(2) : Math.round(fallback));
  }

  /* Convert a threshold amount in CAD cents to display string in user's currency.
     Always whole numbers — no .99 rounding on threshold labels. */
  function moneyThresh(cadCents) {
    var amount  = cadCents / 100;
    var dgc     = window.DoublyGlobalCurrency;
    var shopCur = getShopCurrency();

    if (dgc && dgc.currentCurrency && dgc.currentCurrency !== shopCur && typeof dgc.convert === 'function') {
      try {
        var converted = dgc.convert(amount, shopCur, dgc.currentCurrency);
        return _formatConverted(Math.round(converted), dgc.currentCurrency);
      } catch(e) {}
    }
    if (window.Intl && window.Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency', currency: shopCur,
          minimumFractionDigits: 0, maximumFractionDigits: 0
        }).format(amount);
      } catch(e) {}
    }
    return '$' + amount.toFixed(0);
  }
  function esc(str) {
    return String(str)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /* ============================================================
     DAWN ATC INTERCEPTION — multi-strategy
  ============================================================ */
  function interceptDawnATC() {

    /* Strategy 1: capture-phase form submit */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var fd = new FormData(form);
      fetch(ADD_URL, {
        method:  'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body:    fd,
      }).then(function (r) {
        if (!r.ok) return;
        r.clone().json().then(function(data) {
          var items = data.items || [data];
          var tagFetches = items
            .filter(function(item) { return item && item.product_id && item.handle && !tagCache[String(item.product_id)]; })
            .map(function(item) {
              return fetch('/products/' + item.handle + '.js')
                .then(function(r2){ return r2.json(); })
                .then(function(p){ if (p && p.id) tagCache[String(p.id)] = Array.isArray(p.tags) ? p.tags.join(',') : (p.tags||''); })
                .catch(function(){});
            });
          Promise.all(tagFetches).then(function() {
            showToast('✦ Added to your vault');
            openDrawer();
          });
        }).catch(function(){ showToast('✦ Added to your vault'); openDrawer(); });
      });
    }, true);

    /* Strategy 2: intercept Dawn's fetch calls by monkey-patching
       window.fetch so we can detect /cart/add calls made by
       product-form web component and open our drawer after them */
    var _realFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var promise = _realFetch.apply(this, arguments);

      if (url.indexOf('/cart/add') !== -1) {
        promise.then(function (response) {
          if (response.ok) {
            // Clone so original consumer can still read body
            response.clone().json().then(function(data) {
              var items = data.items || [data];
              // Fetch tags for all new items, THEN open drawer
              var tagFetches = items
                .filter(function(item) { return item && item.product_id && item.handle && !tagCache[String(item.product_id)]; })
                .map(function(item) {
                  return _realFetch('/products/' + item.handle + '.js')
                    .then(function(r){ return r.json(); })
                    .then(function(p){
                      if (p && p.id) {
                        tagCache[String(p.id)] = Array.isArray(p.tags) ? p.tags.join(',') : (p.tags || '');
                      }
                    }).catch(function(){});
                });
              // Wait for all tag fetches to complete, then open
              Promise.all(tagFetches).then(function() {
                showToast('✦ Added to your vault');
                openDrawer();
              });
            }).catch(function(){
              // If clone/parse fails, open anyway
              showToast('✦ Added to your vault');
              openDrawer();
            });
          }
          return response;
        }).catch(function () {});
      }

      if (url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1) {
        promise.then(function (response) {
          if (response.ok) fetchAndRender();
          return response;
        }).catch(function () {});
      }

      return promise;
    };

    /* Strategy 3: kill Dawn notification elements as they appear */
    var mo = new MutationObserver(function () {
      ['cart-notification', '.cart-notification', '#cart-notification', 'cart-drawer'].forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          if (el.id !== 'ol-cart-drawer') {
            el.style.cssText = 'display:none!important;visibility:hidden!important;pointer-events:none!important;';
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','style','open','active'] });

    /* Strategy 4: listen for Shopify's cart:add custom event
       (fired by newer Dawn versions) */
    document.addEventListener('cart:add', function () {
      showToast('✦ Added to your vault');
      openDrawer();
    });
  }

  /* ============================================================
     DELEGATED EVENT LISTENERS — wire qty/remove via data attrs
     (avoids inline onclick breakage on mobile)
  ============================================================ */
  function wireCartEvents() {
    var body = $id('ol-drawer-body');
    if (!body) return;

    body.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.getAttribute('data-action');
      var key    = btn.getAttribute('data-key');
      var qty    = parseInt(btn.getAttribute('data-qty'), 10);

      if (action === 'decrease' || action === 'increase') {
        e.preventDefault();
        changeQty(key, qty);
      }
      if (action === 'remove') {
        e.preventDefault();
        changeQty(key, 0);
      }
      if (action === 'upsell-add') {
        e.preventDefault();
        var vid = parseInt(btn.getAttribute('data-variant-id'), 10);
        addUpsell(vid, btn);
      }
    });

    // Touch support for mobile checkout button
    var checkoutBtn = $id('ol-checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('touchend', function (e) {
        e.preventDefault();
        window.location.href = checkoutBtn.href || CHECKOUT_URL;
      });
    }
  }

  /* ============================================================
     INIT
  ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {

    /* Seed tagCache from Liquid-rendered cart items */
    document.querySelectorAll('#ol-cart-items .ol-cart-item[data-product-id][data-tags]').forEach(function(el) {
      var pid  = el.getAttribute('data-product-id');
      var tags = el.getAttribute('data-tags');
      if (pid && tags) tagCache[pid] = tags;
    });
    /* Also merge the inline bootstrap cache injected by Liquid */
    if (window.__OLTagCache) {
      Object.keys(window.__OLTagCache).forEach(function(pid) {
        tagCache[pid] = window.__OLTagCache[pid];
      });
    }

    /* Overlay + close button */
    var ov  = $id('ol-cart-overlay');
    var cl  = $id('ol-close-btn');
    var cnt = $id('ol-continue-btn');
    if (ov)  ov.addEventListener('click',  closeDrawer);
    if (cl)  cl.addEventListener('click',  closeDrawer);
    if (cnt) cnt.addEventListener('click', closeDrawer);

    /* Cart icon buttons */
    document.querySelectorAll('[data-ol-open-cart]').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); openDrawer(); });
    });

    /* Re-render prices when Beast Currency Converter (DoublyGlobalCurrency) changes currency.
       Desktop: Beast fires jQuery click on .currency-switcher-btn
       Mobile:  Beast uses <select name="doubly-currencies"> which fires 'change' */
    var _lastCurrency = window.DoublyGlobalCurrency ? window.DoublyGlobalCurrency.currentCurrency : null;

    function _onCurrencyChange() {
      setTimeout(function() {
        var dgc = window.DoublyGlobalCurrency;
        if (dgc && dgc.currentCurrency !== _lastCurrency) {
          _lastCurrency = dgc.currentCurrency;
          if (drawerOpen) fetchAndRender();
          // Always update the currency note even if drawer is closed
          var currNote  = document.getElementById('ol-currency-note');
          var currLabel = document.getElementById('ol-currency-label');
          if (currNote && currLabel) {
            var shopCur = getShopCurrency();
            if (dgc.currentCurrency && dgc.currentCurrency !== shopCur) {
              currLabel.textContent = dgc.currentCurrency;
              currNote.style.display = '';
            } else {
              currNote.style.display = 'none';
            }
          }
        }
      }, 150);
    }

    // Desktop: click on Beast's custom dropdown buttons
    document.addEventListener('click', function(e) {
      if (e.target.closest('.currency-switcher-btn') || e.target.closest('.doubly-nice-select')) {
        _onCurrencyChange();
      }
    });

    // Mobile: native select change
    document.addEventListener('change', function(e) {
      if (e.target && e.target.name === 'doubly-currencies') {
        _onCurrencyChange();
      }
    });

    /* Wire delegated events */
    wireCartEvents();

    /* Intercept Dawn ATC */
    interceptDawnATC();

    /* Initial badge sync without opening drawer */
    fetch(CART_URL, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastItemCount = cart.item_count;
        lastCartTotal = cart.total_price;
        updateNavBadge(cart);
      });

  });

}());
